import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { clearTokens, getToken, setPersistentToken } from '../../../shared/services/token';

const API = process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Load the signed-in user. Returns 'ok', 'invalid' (the server rejected the
   * token) or 'unreachable' (network error or server error).
   *
   * Only 'invalid' may end the session. A backend that is restarting used to
   * answer one request with a connection reset, and that alone signed people out.
   */
  const fetchMe = useCallback(async (token) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (attempt) await new Promise(r => setTimeout(r, 1500));
      try {
        const res = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          setUser(await res.json());
          return 'ok';
        }
        if (res.status === 401 || res.status === 403) return 'invalid';
      } catch (_) { /* unreachable — try again */ }
    }
    return 'unreachable';
  }, []);

  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchMe(token).then(result => {
        if (result === 'invalid') clearTokens();
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  const loginWithToken = async (token) => {
    setPersistentToken(token);
    await fetchMe(token);
  };

  const logout = () => {
    clearTokens();
    // Any impersonation leftovers go too, so the next person to sign in on this
    // browser never inherits a banner or a stashed platform session.
    localStorage.removeItem('recruit_platform_token');
    localStorage.removeItem('recruit_impersonating');
    // The view choice belongs to the person, not the browser.
    localStorage.removeItem('recruit_view_mode');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, loginWithToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/** Returns the stored JWT token (for API calls). */
export const getAuthToken = () => getToken();

